import type { ReactElement, ReactNode } from 'react';
import { createElement, Children, isValidElement } from 'react';
import cx from 'clsx';
import {
  IconWarningSignFilled,
  IconInfoRoundedFilled,
  IconErrorFilled,
  IconCheckmarkRoundedFilled,
} from '@repo/monochrome-icons';
import styles from './disclaimer.module.css';
import { PromoIcon } from './icons/promo';

export interface DisclaimerProps {
  children: ReactNode;
  level?: 'info' | 'warning' | 'error' | 'promo' | 'success';
  block?: boolean;
  className?: string;
}

// TODO: change alt to translation when i18n
const ICONS = {
  info: { element: IconInfoRoundedFilled, alt: 'Info' },
  warning: { element: IconWarningSignFilled, alt: 'Warning' },
  error: { element: IconErrorFilled, alt: 'Error' },
  promo: { element: PromoIcon, alt: 'Promo' },
  success: { element: IconCheckmarkRoundedFilled, alt: 'Success' },
};

function DisclaimerComponent({
  children,
  level = 'info',
  block = false,
  className,
  ...props
}: DisclaimerProps): ReactElement {
  const hasBlockContent = Children.toArray(children).some(
    child =>
      isValidElement(child) &&
      typeof child.type === 'string' &&
      ['div', 'p', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(child.type)
  );

  const MessageTag = hasBlockContent ? 'div' : 'p';

  return (
    <div
      className={cx(
        styles.wrapper,
        block && styles.block,
        !block && styles.inline,
        block && styles[`${level}Block`],
        className
      )}
      data-test-level={level}
      {...props}
    >
      <div className={cx(styles.icon, styles[`${level}Icon`])}>
        {createElement(ICONS[level].element, {
          'aria-label': ICONS[level].alt,
        })}
      </div>
      <MessageTag className={cx('body-standard', styles.message)} data-test-message>
        {children}
      </MessageTag>
    </div>
  );
}

function DisclaimerInline({
  level = 'info',
  className,
  children,
  ...props
}: DisclaimerProps): ReactElement {
  return (
    <DisclaimerComponent
      block={false}
      className={className}
      data-test-disclaimer-inline
      level={level}
      {...props}
    >
      {children}
    </DisclaimerComponent>
  );
}

function DisclaimerBlock({
  level = 'info',
  className,
  children,
  ...props
}: DisclaimerProps): ReactElement {
  return (
    <DisclaimerComponent
      block
      className={className}
      data-test-disclaimer-block
      level={level}
      {...props}
    >
      {children}
    </DisclaimerComponent>
  );
}

export const Disclaimer = {
  Inline: DisclaimerInline,
  Block: DisclaimerBlock,
};
